import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  CheckIcon,
  FileXlsIcon,
  LinkIcon,
  UploadIcon,
  ArrowSquareOutIcon,
  CreditCardIcon,
  FileTextIcon,
  ChartBarIcon,
  CalendarIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { Progress } from 'src/components/ui/progress';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { cn } from 'src/lib/utils';
import type {
  GetAgreementImportContextResponse,
  ParseHistoricalCsvResponse,
  ParsePaymentFilesResponse,
  ParseReportedFileResponse,
} from '@erp/rpc';

// Transaction Import Step Components
import Step1SelectAgreement from './steps/Step1SelectAgreement';
import Step2ParsePreview from './steps/Step2ParsePreview';
import Step3ProductMapping from './steps/Step3ProductMapping';

// Payment Import Step Components
import PaymentStep1Upload from './steps/payments/PaymentStep1Upload';
import PaymentStep3Results from './steps/payments/PaymentStep3Results';

// Reported Import Step Components
import ReportedStep1Upload from './steps/reported/ReportedStep1Upload';
import type {
  SupplierMappingEntry as ReportedSupplierMappingEntry,
  DivisionMappingEntry as ReportedDivisionMappingEntry,
} from './steps/reported/ReportedStep1Upload';
import ReportedStep2Results from './steps/reported/ReportedStep2Results';

// ============================================================================
// Types
// ============================================================================

type ImportFlowType = 'select' | 'transactions' | 'payments' | 'reported';

interface TransactionImportState {
  currentStep: number;
  agreementContext: GetAgreementImportContextResponse | null;
  csvData: ParseHistoricalCsvResponse | null;
  year: number;
  completedSteps: Set<number>;
  isLoading: boolean;
  error?: string;
}

interface SupplierMappingEntry {
  csvSupplierName: string;
  supplierId: string;
}

interface PaymentImportState {
  currentStep: number;
  parseResult: ParsePaymentFilesResponse | null;
  paymentsCsvBase64: string;
  supplierMappings: SupplierMappingEntry[];
  completedSteps: Set<number>;
  isLoading: boolean;
  error?: string;
  importComplete: boolean;
}

interface ReportedImportState {
  currentStep: number;
  parseResult: ParseReportedFileResponse | null;
  xlsxBase64: string;
  fileName: string;
  targetMonth: string; // YYYY-MM format
  supplierMappings: ReportedSupplierMappingEntry[];
  divisionMappings: ReportedDivisionMappingEntry[];
  completedSteps: Set<number>;
  isLoading: boolean;
  error?: string;
  importComplete: boolean;
}

// ============================================================================
// Step Configurations
// ============================================================================

const TRANSACTION_STEPS = [
  {
    id: 1,
    title: 'Select Agreement',
    description: 'Choose agreement and verify rexel code',
    icon: LinkIcon,
  },
  {
    id: 2,
    title: 'Parse CSV',
    description: 'Preview filtered transactions and product codes',
    icon: FileXlsIcon,
  },
  {
    id: 3,
    title: 'Map & Import',
    description: 'Map product codes and import transactions',
    icon: UploadIcon,
  },
];

const PAYMENT_STEPS = [
  {
    id: 1,
    title: 'Upload & Map Suppliers',
    description: 'Upload CSV and confirm supplier matches',
    icon: UploadIcon,
  },
  {
    id: 2,
    title: 'Import',
    description: 'Import payments to agreements',
    icon: CreditCardIcon,
  },
];

const REPORTED_STEPS = [
  {
    id: 1,
    title: 'Upload & Map',
    description: 'Upload xlsx and confirm mappings',
    icon: UploadIcon,
  },
  {
    id: 2,
    title: 'Import',
    description: 'Import reported breakdowns',
    icon: ChartBarIcon,
  },
];

// ============================================================================
// Flow Selection Component
// ============================================================================

interface FlowSelectionProps {
  onSelectFlow: (flow: ImportFlowType) => void;
  onCancel: () => void;
}

function FlowSelection({ onSelectFlow, onCancel }: FlowSelectionProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Historical Data Import</h1>
          <p className="text-muted-foreground">
            Choose what type of historical data you want to import
          </p>
        </div>
        <Button onClick={onCancel} variant="outline">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Flow Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card
          className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
          onClick={() => onSelectFlow('transactions')}
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 rounded-full bg-blue-500/10">
                <FileTextIcon className="h-10 w-10 text-blue-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Import Transactions</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Import line item transaction data from Enable exports. This
                  flow imports for a single agreement at a time.
                </p>
              </div>
              <ul className="text-sm text-muted-foreground text-left space-y-1">
                <li>- Select a rebate agreement</li>
                <li>- Parse transactions CSV by supplier code</li>
                <li>- Map products to product groups</li>
                <li>- Create invoice line items</li>
              </ul>
              <Button className="w-full mt-4">Start Transaction Import</Button>
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
          onClick={() => onSelectFlow('payments')}
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 rounded-full bg-green-500/10">
                <CreditCardIcon className="h-10 w-10 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Import Payments</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Import supplier payments and allocations from Enable exports.
                  This flow supports bulk import across multiple suppliers.
                </p>
              </div>
              <ul className="text-sm text-muted-foreground text-left space-y-1">
                <li>- Upload payments CSV</li>
                <li>- Upload allocations CSV</li>
                <li>- Validate suppliers, divisions, agreements</li>
                <li>- Create payment and allocation records</li>
              </ul>
              <Button className="w-full mt-4" variant="outline">
                Start Payments Import
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
          onClick={() => onSelectFlow('reported')}
        >
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 rounded-full bg-purple-500/10">
                <ChartBarIcon className="h-10 w-10 text-purple-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Import Reported Data</h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Import reported accrual data from Enable exports. Creates
                  breakdown records with reported values.
                </p>
              </div>
              <ul className="text-sm text-muted-foreground text-left space-y-1">
                <li>- Upload reported data xlsx</li>
                <li>- Map suppliers and divisions</li>
                <li>- Distribute by rule proportions</li>
                <li>- Create reported breakdowns</li>
              </ul>
              <Button className="w-full mt-4" variant="outline">
                Start Reported Import
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function HistoricalImportPage() {
  const navigate = useNavigate();

  // Flow selection state
  const [flowType, setFlowType] = useState<ImportFlowType>('select');

  // Transaction import state
  const [transactionState, setTransactionState] =
    useState<TransactionImportState>({
      currentStep: 1,
      agreementContext: null,
      csvData: null,
      year: 2025,
      completedSteps: new Set(),
      isLoading: false,
    });

  // Payment import state
  const [paymentState, setPaymentState] = useState<PaymentImportState>({
    currentStep: 1,
    parseResult: null,
    paymentsCsvBase64: '',
    supplierMappings: [],
    completedSteps: new Set(),
    isLoading: false,
    importComplete: false,
  });

  // Reported import state
  const [reportedState, setReportedState] = useState<ReportedImportState>({
    currentStep: 1,
    parseResult: null,
    xlsxBase64: '',
    fileName: '',
    targetMonth: '', // Will be set by user
    supplierMappings: [],
    divisionMappings: [],
    completedSteps: new Set(),
    isLoading: false,
    importComplete: false,
  });

  // ============================================================================
  // Common Handlers
  // ============================================================================

  const handleCancel = () => {
    navigate({ to: '/rebates/agreements' });
  };

  const handleBackToSelection = () => {
    setFlowType('select');
    // Reset states
    setTransactionState({
      currentStep: 1,
      agreementContext: null,
      csvData: null,
      year: 2025,
      completedSteps: new Set(),
      isLoading: false,
    });
    setPaymentState({
      currentStep: 1,
      parseResult: null,
      paymentsCsvBase64: '',
      supplierMappings: [],
      completedSteps: new Set(),
      isLoading: false,
      importComplete: false,
    });
    setReportedState({
      currentStep: 1,
      parseResult: null,
      xlsxBase64: '',
      fileName: '',
      targetMonth: '',
      supplierMappings: [],
      divisionMappings: [],
      completedSteps: new Set(),
      isLoading: false,
      importComplete: false,
    });
  };

  // ============================================================================
  // Transaction Flow Handlers
  // ============================================================================

  const handleTransactionBack = () => {
    if (transactionState.currentStep > 1) {
      setTransactionState((prev) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }));
    } else {
      handleBackToSelection();
    }
  };

  const handleTransactionStep1Complete = (
    context: GetAgreementImportContextResponse,
    year: number,
  ) => {
    setTransactionState((prev) => ({
      ...prev,
      agreementContext: context,
      year,
      currentStep: 2,
      completedSteps: new Set([...prev.completedSteps, 1]),
      error: undefined,
    }));
  };

  const handleTransactionStep2Complete = (
    csvData: ParseHistoricalCsvResponse,
  ) => {
    setTransactionState((prev) => ({
      ...prev,
      csvData,
      currentStep: 3,
      completedSteps: new Set([...prev.completedSteps, 2]),
      error: undefined,
    }));
  };

  const handleTransactionStep3Complete = () => {
    setTransactionState((prev) => ({
      ...prev,
      completedSteps: new Set([...prev.completedSteps, 3]),
      error: undefined,
    }));
    if (transactionState.agreementContext) {
      navigate({
        to: '/rebates/agreements/$id',
        params: { id: transactionState.agreementContext.agreementId },
      });
    }
  };

  const handleTransactionError = (error: string) => {
    setTransactionState((prev) => ({ ...prev, error, isLoading: false }));
  };

  // ============================================================================
  // Payment Flow Handlers
  // ============================================================================

  const handlePaymentBack = () => {
    if (paymentState.currentStep > 1) {
      setPaymentState((prev) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }));
    } else {
      handleBackToSelection();
    }
  };

  const handlePaymentStep1Complete = (
    parseResult: ParsePaymentFilesResponse,
    paymentsCsvBase64: string,
    supplierMappings: SupplierMappingEntry[],
  ) => {
    setPaymentState((prev) => ({
      ...prev,
      parseResult,
      paymentsCsvBase64,
      supplierMappings,
      currentStep: 2,
      completedSteps: new Set([...prev.completedSteps, 1]),
      error: undefined,
    }));
  };

  const handlePaymentStep2Complete = () => {
    setPaymentState((prev) => ({
      ...prev,
      currentStep: 3,
      completedSteps: new Set([...prev.completedSteps, 2]),
      error: undefined,
    }));
  };

  const handlePaymentStep3Complete = () => {
    setPaymentState((prev) => ({
      ...prev,
      completedSteps: new Set([...prev.completedSteps, 3]),
      importComplete: true,
      error: undefined,
    }));
  };

  const handlePaymentError = (error: string) => {
    setPaymentState((prev) => ({ ...prev, error, isLoading: false }));
  };

  const handlePaymentDone = () => {
    navigate({ to: '/rebates/agreements' });
  };

  // ============================================================================
  // Reported Flow Handlers
  // ============================================================================

  const handleReportedBack = () => {
    if (reportedState.currentStep > 1) {
      setReportedState((prev) => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }));
    } else {
      handleBackToSelection();
    }
  };

  const handleReportedStep1Complete = (
    parseResult: ParseReportedFileResponse,
    xlsxBase64: string,
    fileName: string,
    targetMonth: string,
    supplierMappings: ReportedSupplierMappingEntry[],
    divisionMappings: ReportedDivisionMappingEntry[],
  ) => {
    setReportedState((prev) => ({
      ...prev,
      parseResult,
      xlsxBase64,
      fileName,
      targetMonth,
      supplierMappings,
      divisionMappings,
      currentStep: 2,
      completedSteps: new Set([...prev.completedSteps, 1]),
      error: undefined,
    }));
  };

  const handleReportedStep2Complete = () => {
    setReportedState((prev) => ({
      ...prev,
      completedSteps: new Set([...prev.completedSteps, 2]),
      importComplete: true,
      error: undefined,
    }));
  };

  const handleReportedError = (error: string) => {
    setReportedState((prev) => ({ ...prev, error, isLoading: false }));
  };

  const handleReportedDone = () => {
    navigate({ to: '/rebates/agreements' });
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Flow selection screen
  if (flowType === 'select') {
    return (
      <DashboardLayout>
        <div className="p-6">
          <FlowSelection onSelectFlow={setFlowType} onCancel={handleCancel} />
        </div>
      </DashboardLayout>
    );
  }

  // Transaction import flow
  if (flowType === 'transactions') {
    const progress =
      (transactionState.currentStep / TRANSACTION_STEPS.length) * 100;

    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Import Transactions</h1>
                <p className="text-muted-foreground">
                  Import historical line item transactions from Enable CSV
                </p>
              </div>
              <Button onClick={handleBackToSelection} variant="outline">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Selected Agreement Display */}
            {transactionState.agreementContext && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Selected Agreement
                      </p>
                      <h2 className="text-xl font-semibold">
                        {transactionState.agreementContext.agreementName}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {transactionState.agreementContext.supplierName}
                        {transactionState.agreementContext.companyName &&
                          ` | ${transactionState.agreementContext.companyName}`}
                        {transactionState.year &&
                          ` | Year ${transactionState.year}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(
                          `/rebates/agreements/${transactionState.agreementContext?.agreementId}`,
                          '_blank',
                        );
                      }}
                    >
                      <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
                      Open Agreement
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Progress Bar */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm text-muted-foreground">
                      Step {transactionState.currentStep} of{' '}
                      {TRANSACTION_STEPS.length}
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Stepper */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  {TRANSACTION_STEPS.map((step, index) => {
                    const isActive = transactionState.currentStep === step.id;
                    const isCompleted = transactionState.currentStep > step.id;
                    const Icon = step.icon;

                    return (
                      <div key={step.id} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                              isCompleted
                                ? 'bg-primary border-primary text-primary-foreground'
                                : isActive
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'bg-background border-muted-foreground text-muted-foreground',
                            )}
                          >
                            {isCompleted ? (
                              <CheckIcon className="h-5 w-5" />
                            ) : (
                              <Icon className="h-5 w-5" />
                            )}
                          </div>
                          <div className="mt-2 text-center">
                            <p
                              className={cn(
                                'text-sm font-medium',
                                isActive
                                  ? 'text-primary'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {step.title}
                            </p>
                            <p className="text-xs text-muted-foreground max-w-[120px]">
                              {step.description}
                            </p>
                          </div>
                        </div>
                        {index < TRANSACTION_STEPS.length - 1 && (
                          <div className="flex-1 h-px bg-border mx-4" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Step Content */}
            <Card>
              <CardContent className="p-6">
                {transactionState.currentStep === 1 && (
                  <Step1SelectAgreement
                    onComplete={handleTransactionStep1Complete}
                    onError={handleTransactionError}
                    isLoading={transactionState.isLoading}
                    error={transactionState.error}
                  />
                )}
                {transactionState.currentStep === 2 &&
                  transactionState.agreementContext && (
                    <Step2ParsePreview
                      agreementContext={transactionState.agreementContext}
                      year={transactionState.year}
                      onComplete={handleTransactionStep2Complete}
                      onBack={handleTransactionBack}
                      onError={handleTransactionError}
                      isLoading={transactionState.isLoading}
                      error={transactionState.error}
                    />
                  )}
                {transactionState.currentStep === 3 &&
                  transactionState.agreementContext &&
                  transactionState.csvData && (
                    <Step3ProductMapping
                      agreementContext={transactionState.agreementContext}
                      csvData={transactionState.csvData}
                      year={transactionState.year}
                      onComplete={handleTransactionStep3Complete}
                      onBack={handleTransactionBack}
                      onError={handleTransactionError}
                      isLoading={transactionState.isLoading}
                      error={transactionState.error}
                    />
                  )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Payment import flow
  if (flowType === 'payments') {
    const progress = (paymentState.currentStep / PAYMENT_STEPS.length) * 100;

    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Import Payments</h1>
                <p className="text-muted-foreground">
                  Import supplier payments and allocations from Enable CSV
                </p>
              </div>
              <Button onClick={handleBackToSelection} variant="outline">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Progress Bar */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm text-muted-foreground">
                      Step {paymentState.currentStep} of {PAYMENT_STEPS.length}
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Stepper */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  {PAYMENT_STEPS.map((step, index) => {
                    const isActive = paymentState.currentStep === step.id;
                    const isCompleted = paymentState.currentStep > step.id;
                    const Icon = step.icon;

                    return (
                      <div key={step.id} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                              isCompleted
                                ? 'bg-primary border-primary text-primary-foreground'
                                : isActive
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'bg-background border-muted-foreground text-muted-foreground',
                            )}
                          >
                            {isCompleted ? (
                              <CheckIcon className="h-5 w-5" />
                            ) : (
                              <Icon className="h-5 w-5" />
                            )}
                          </div>
                          <div className="mt-2 text-center">
                            <p
                              className={cn(
                                'text-sm font-medium',
                                isActive
                                  ? 'text-primary'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {step.title}
                            </p>
                            <p className="text-xs text-muted-foreground max-w-[120px]">
                              {step.description}
                            </p>
                          </div>
                        </div>
                        {index < PAYMENT_STEPS.length - 1 && (
                          <div className="flex-1 h-px bg-border mx-4" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Step Content */}
            <Card>
              <CardContent className="p-6">
                {paymentState.currentStep === 1 && (
                  <PaymentStep1Upload
                    onComplete={handlePaymentStep1Complete}
                    onBack={handlePaymentBack}
                    onError={handlePaymentError}
                    isLoading={paymentState.isLoading}
                    error={paymentState.error}
                  />
                )}
                {paymentState.currentStep === 2 && (
                  <PaymentStep3Results
                    paymentsCsvBase64={paymentState.paymentsCsvBase64}
                    supplierMappings={paymentState.supplierMappings}
                    onComplete={handlePaymentStep2Complete}
                    onDone={handlePaymentDone}
                    onError={handlePaymentError}
                    isLoading={paymentState.isLoading}
                    error={paymentState.error}
                    importComplete={paymentState.importComplete}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Reported import flow
  if (flowType === 'reported') {
    const progress = (reportedState.currentStep / REPORTED_STEPS.length) * 100;

    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Import Reported Data</h1>
                <p className="text-muted-foreground">
                  Import reported accrual breakdowns from Enable xlsx
                </p>
              </div>
              <Button onClick={handleBackToSelection} variant="outline">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Import Context Display */}
            {(reportedState.targetMonth || reportedState.fileName) && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Target Month
                        </p>
                        <p className="font-semibold">
                          {reportedState.targetMonth
                            ? (() => {
                                const [year, month] =
                                  reportedState.targetMonth.split('-');
                                return new Date(
                                  Number(year),
                                  Number(month) - 1,
                                ).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                });
                              })()
                            : '-'}
                        </p>
                      </div>
                    </div>
                    {reportedState.fileName && (
                      <div className="flex items-center gap-2">
                        <FileXlsIcon className="h-5 w-5 text-primary" />
                        <div>
                          <p className="text-xs text-muted-foreground">File</p>
                          <p className="font-semibold">
                            {reportedState.fileName}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Progress Bar */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm text-muted-foreground">
                      Step {reportedState.currentStep} of{' '}
                      {REPORTED_STEPS.length}
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Stepper */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  {REPORTED_STEPS.map((step, index) => {
                    const isActive = reportedState.currentStep === step.id;
                    const isCompleted = reportedState.currentStep > step.id;
                    const Icon = step.icon;

                    return (
                      <div key={step.id} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                              isCompleted
                                ? 'bg-primary border-primary text-primary-foreground'
                                : isActive
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'bg-background border-muted-foreground text-muted-foreground',
                            )}
                          >
                            {isCompleted ? (
                              <CheckIcon className="h-5 w-5" />
                            ) : (
                              <Icon className="h-5 w-5" />
                            )}
                          </div>
                          <div className="mt-2 text-center">
                            <p
                              className={cn(
                                'text-sm font-medium',
                                isActive
                                  ? 'text-primary'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {step.title}
                            </p>
                            <p className="text-xs text-muted-foreground max-w-[120px]">
                              {step.description}
                            </p>
                          </div>
                        </div>
                        {index < REPORTED_STEPS.length - 1 && (
                          <div className="flex-1 h-px bg-border mx-4" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Step Content */}
            <Card>
              <CardContent className="p-6">
                {reportedState.currentStep === 1 && (
                  <ReportedStep1Upload
                    onComplete={handleReportedStep1Complete}
                    onBack={handleReportedBack}
                    onError={handleReportedError}
                    isLoading={reportedState.isLoading}
                    error={reportedState.error}
                  />
                )}
                {reportedState.currentStep === 2 && (
                  <ReportedStep2Results
                    xlsxBase64={reportedState.xlsxBase64}
                    fileName={reportedState.fileName}
                    targetMonth={reportedState.targetMonth}
                    supplierMappings={reportedState.supplierMappings}
                    divisionMappings={reportedState.divisionMappings}
                    onComplete={handleReportedStep2Complete}
                    onDone={handleReportedDone}
                    onError={handleReportedError}
                    isLoading={reportedState.isLoading}
                    error={reportedState.error}
                    importComplete={reportedState.importComplete}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return null;
}
